import styled from '@emotion/styled';
import { useState, useMemo } from 'react';
import { ChevronLeft, Star } from 'lucide-react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';
import type { ProductVariant } from '@/domain/entities/product.entity';
import { useProduct } from '@/lib/hooks/use-product';

const DESKTOP_BREAKPOINT = '768px';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 0 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

const PageTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    font-size: 24px;
    line-height: 1.33;
  }
`;

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    flex-direction: row;
    gap: 32px;
    align-items: flex-start;
  }
`;

const ImageSection = styled.div`
  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    flex: 0 0 50%;
    max-width: 50%;
    position: sticky;
    top: 24px;
  }
`;

const DetailSection = styled.div`
  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    flex: 1;
    min-width: 0;
  }
`;

const ImageCarousel = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background: #f7f7f7;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    aspect-ratio: 3 / 4;
    border-radius: 12px;
  }
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 8px 0 16px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${({ active }) => (active ? '#292929' : '#d1d1d1')};
  padding: 0;
  cursor: pointer;
`;

const ProductSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProductName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.44;
  color: #292929;
  margin: 0;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    font-size: 20px;
  }
`;

const AutoshipTag = styled.span`
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StarIcon = styled.span`
  display: flex;
  align-items: center;
  color: #fbbf24;
`;

const RatingText = styled.span`
  font-size: 13px;
  color: #5a5a5a;
`;

const OptionSection = styled.div`
  padding: 12px 0;
  border-top: 1px solid #f0f0f0;
`;

const OptionLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #292929;
  display: block;
  margin-bottom: 10px;
`;

const ColorSwatches = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ColorSwatch = styled.button<{ swatchColor: string; selected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${({ selected }) => (selected ? '#292929' : 'transparent')};
  padding: 2px;
  cursor: pointer;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    display: block;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${({ swatchColor }) => swatchColor};
    border: 1px solid #d1d1d1;
  }
`;

const SizeGrid = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SizeButton = styled.button<{ selected: boolean; disabled?: boolean }>`
  min-width: 48px;
  padding: 8px 12px;
  border: 1px solid ${({ selected }) => (selected ? '#292929' : '#d1d1d1')};
  border-radius: 6px;
  background: ${({ selected }) => (selected ? '#292929' : '#fff')};
  color: ${({ selected, disabled }) => {
    if (disabled) return '#ccc';
    return selected ? '#fff' : '#292929';
  }};
  font-size: 13px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: #292929;
  }
`;

const SizeGuideLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: #292929;
  text-decoration: underline;
  cursor: pointer;
  margin-bottom: 10px;
  display: block;

  &:hover {
    opacity: 0.7;
  }
`;

const PriceSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
`;

const OriginalPrice = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #999;
  text-decoration: line-through;
`;

const FinalPrice = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #292929;
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  padding-top: 8px;
`;

const ContinueSwapButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 8px;
  background: #292929;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #444;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CancelLink = styled.button`
  background: none;
  border: none;
  padding: 12px 0 24px;
  font-size: 14px;
  font-weight: 500;
  color: #5a5a5a;
  text-decoration: underline;
  cursor: pointer;
  display: block;
  width: 100%;
  text-align: center;

  &:hover {
    color: #292929;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 16px;
  color: #5a5a5a;
  font-size: 14px;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #c53030;
  font-size: 14px;
`;

const COLOR_MAP: Record<string, string> = {
  Black: '#000000',
  Nude: '#deb887',
  Beige: '#f5f5dc',
  White: '#ffffff',
  Pink: '#ffc0cb',
  Navy: '#000080',
  Gray: '#808080',
  Grey: '#808080',
  Brown: '#8b4513',
  Red: '#dc143c',
  Blue: '#4169e1',
  Green: '#228b22',
  Purple: '#800080',
  Burgundy: '#800020',
  Ivory: '#fffff0',
  Mocha: '#967969',
  Taupe: '#483c32',
  Charcoal: '#36454f',
  Rose: '#ff007f',
};

function getSwatchColor(colorName: string): string {
  return COLOR_MAP[colorName] ?? '#d1d1d1';
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

interface SwapProductDetailPageProps {
  readonly productSlug: string;
  readonly subscription: CustomerSubscription;
  readonly onContinueSwap: (
    product: { id: string; title: string; imageUrl?: string },
    variant: ProductVariant,
  ) => void;
  readonly onCancel: () => void;
}

export function SwapProductDetailPage({
  productSlug,
  subscription,
  onContinueSwap,
  onCancel,
}: SwapProductDetailPageProps) {
  const { data: product, isLoading, error } = useProduct(productSlug);

  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const colorOptions = useMemo(() => {
    if (!product) return [];
    const colorOpt = product.options?.find(
      (o) => o.name.toLowerCase() === 'color' || o.name.toLowerCase() === 'colour',
    );
    if (colorOpt?.values?.length) return colorOpt.values;

    const fromVariants = new Set<string>();
    for (const v of product.variants ?? []) {
      for (const a of v.variation_attributes ?? []) {
        if (a.name.toLowerCase() === 'color' || a.name.toLowerCase() === 'colour') {
          fromVariants.add(a.value);
        }
      }
    }
    return Array.from(fromVariants);
  }, [product]);

  const sizeOptions = useMemo(() => {
    if (!product) return [];
    const sizeOpt = product.options?.find(
      (o) => o.name.toLowerCase() === 'size',
    );
    if (sizeOpt?.values?.length) return sizeOpt.values;

    const fromVariants = new Set<string>();
    for (const v of product.variants ?? []) {
      for (const a of v.variation_attributes ?? []) {
        if (a.name.toLowerCase() === 'size') {
          fromVariants.add(a.value);
        }
      }
    }
    return Array.from(fromVariants);
  }, [product]);

  if (product && colorOptions.length > 0 && selectedColor === null) {
    setSelectedColor(colorOptions[0]);
  }
  if (product && sizeOptions.length > 0 && selectedSize === null) {
    setSelectedSize(sizeOptions[0]);
  }

  const resolvedVariant: ProductVariant | null = useMemo(() => {
    if (!product?.variants) return null;
    return (
      product.variants.find((v) => {
        if (!v.enabled) return false;
        const attrs = v.variation_attributes ?? [];
        const colorMatch =
          !selectedColor ||
          attrs.some(
            (a) =>
              (a.name.toLowerCase() === 'color' || a.name.toLowerCase() === 'colour') &&
              a.value === selectedColor,
          );
        const sizeMatch =
          !selectedSize ||
          attrs.some(
            (a) => a.name.toLowerCase() === 'size' && a.value === selectedSize,
          );
        return colorMatch && sizeMatch;
      }) ?? null
    );
  }, [product, selectedColor, selectedSize]);

  const images = product?.images ?? [];
  const displayedImage = images[currentImageIndex]?.src;

  const handleContinueSwap = () => {
    if (!resolvedVariant || !product) return;
    onContinueSwap(
      {
        id: product.id,
        title: product.title,
        imageUrl: images[0]?.src,
      },
      resolvedVariant,
    );
  };

  if (isLoading) {
    return <LoadingState>Loading product details...</LoadingState>;
  }

  if (error || !product) {
    return (
      <Container>
        <HeaderBar>
          <BackButton onClick={onCancel} aria-label="Back to product list">
            <ChevronLeft size={22} />
          </BackButton>
          <PageTitle>Product Details</PageTitle>
        </HeaderBar>
        <ErrorState>Unable to load product details.</ErrorState>
      </Container>
    );
  }

  const displayPrice = resolvedVariant?.price ?? product.price;
  const comparePrice = resolvedVariant?.compare_at_price ?? product.compare_at_price;

  return (
    <Container>
      <HeaderBar>
        <BackButton onClick={onCancel} aria-label="Back to product list">
          <ChevronLeft size={22} />
        </BackButton>
        <PageTitle>Product Details</PageTitle>
      </HeaderBar>

      <ContentLayout>
        <ImageSection>
          <ImageCarousel>
            {displayedImage ? (
              <CarouselImage src={displayedImage} alt={product.title} />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#999',
                }}
              >
                No image
              </div>
            )}
          </ImageCarousel>

          {images.length > 1 && (
            <CarouselDots>
              {images.map((img, idx) => (
                <Dot
                  key={img.id}
                  active={idx === currentImageIndex}
                  onClick={() => setCurrentImageIndex(idx)}
                  aria-label={`View image ${idx + 1}`}
                />
              ))}
            </CarouselDots>
          )}
        </ImageSection>

        <DetailSection>
          <ProductSection>
            <TitleRow>
              <ProductName>{product.title}</ProductName>
              <AutoshipTag>Autoship</AutoshipTag>
            </TitleRow>

            {product.rating && (
              <RatingRow>
                <StarIcon>
                  <Star size={14} fill="#fbbf24" stroke="#fbbf24" />
                </StarIcon>
                <RatingText>
                  {product.rating.average.toFixed(1)} ({product.rating.count})
                </RatingText>
              </RatingRow>
            )}
          </ProductSection>

          {colorOptions.length > 0 && (
            <OptionSection>
              <OptionLabel>Color: {selectedColor ?? ''}</OptionLabel>
              <ColorSwatches>
                {colorOptions.map((color) => (
                  <ColorSwatch
                    key={color}
                    swatchColor={getSwatchColor(color)}
                    selected={selectedColor === color}
                    onClick={() => setSelectedColor(color)}
                    aria-label={`Select color ${color}`}
                    aria-pressed={selectedColor === color}
                  />
                ))}
              </ColorSwatches>
            </OptionSection>
          )}

          {sizeOptions.length > 0 && (
            <OptionSection>
              <SizeGuideLink type="button">Size Guide</SizeGuideLink>
              <OptionLabel>Size: {selectedSize ?? ''}</OptionLabel>
              <SizeGrid>
                {sizeOptions.map((size) => (
                  <SizeButton
                    key={size}
                    selected={selectedSize === size}
                    onClick={() => setSelectedSize(size)}
                    aria-label={`Select size ${size}`}
                    aria-pressed={selectedSize === size}
                  >
                    {size}
                  </SizeButton>
                ))}
              </SizeGrid>
            </OptionSection>
          )}

          <PriceSection>
            {comparePrice && comparePrice.display > displayPrice.display && (
              <OriginalPrice>{formatCents(comparePrice.display)}</OriginalPrice>
            )}
            <FinalPrice>{formatCents(displayPrice.display)}</FinalPrice>
          </PriceSection>

          <CTASection>
            <ContinueSwapButton
              type="button"
              onClick={handleContinueSwap}
              disabled={!resolvedVariant}
              aria-label="Continue to swap product"
            >
              Continue to swap
            </ContinueSwapButton>

            <CancelLink type="button" onClick={onCancel}>
              Cancel
            </CancelLink>
          </CTASection>
        </DetailSection>
      </ContentLayout>
    </Container>
  );
}
